import { Download01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, Link, useForm } from "@inertiajs/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
	type Conflict,
	ConflictDialog,
	findConflicts,
	mergeValues,
} from "@/components/conflict-dialog";
import type { Country } from "@/components/country-select";
import Dashboard from "@/components/dashboard";
import {
	type EditingPerson,
	EditingPersonItem,
} from "@/components/editing-person-item";
import {
	PERSON_FIELD_LABELS,
	type PersonFormData,
	PersonFormFields,
} from "@/components/person-form-fields";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	countries: Country[];
}

interface FormData {
	ico: string | null;
	physical_person: PersonFormData;
}

export default function NewIndividualEntrepreneur({ user, countries }: Props) {
	const [editingPerson, setEditingPerson] = useState<EditingPerson>();
	const [pending, setPending] = useState<{
		conflicts: Conflict[];
		mergedValues: PersonFormData;
		description: string;
	}>();

	const { data, setData, post, processing, errors, clearErrors, transform } =
		useForm<FormData>({
			ico: null,
			physical_person: {
				first_name: "",
				last_name: "",
				birth_number: "",
				street: null,
				city: null,
				postal_code: null,
				country: "CZ",
				document_type: null,
				document_number: null,
				document_expiry_date: null,
				document_issuer: null,
				birth_date: null,
				birth_place: null,
				birth_country: "CZ",
				citizenship: null,
				citizenship_other: null,
				gender: null,
				is_sanctioned: null,
				is_pep: null,
				pep_info: null,
			},
		});

	const aresMutation = useMutation({
		mutationFn: async (ico: string) => {
			const response = await fetch(`/ares/lookup_rzp?ico=${ico}`);
			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Nepodařilo se načíst data z ARES");
			}
			return response.json();
		},
		onSuccess: (result) => {
			const entity = result.entity;
			applyOrShowConflicts(
				{
					...entity,
					birth_number: "",
					document_type: null,
					document_number: null,
					document_expiry_date: null,
					document_issuer: null,
				},
				"Údaje byly načteny z ARES.",
			);
		},
		onError: (error: Error) => {
			toast.error(error.message);
		},
	});

	const defaults: Partial<PersonFormData> = { country: "CZ" };

	const applyOrShowConflicts = (
		values: PersonFormData,
		description: string,
	) => {
		const currentValues = data.physical_person;
		const merged = mergeValues(currentValues, values);
		const conflicts = findConflicts(
			currentValues,
			values,
			PERSON_FIELD_LABELS,
			defaults,
		);
		if (conflicts.length === 0) {
			setData("physical_person", merged);
		} else {
			setPending({
				conflicts,
				mergedValues: merged,
				description,
			});
		}
	};

	const handleApply = () => {
		if (pending) setData("physical_person", pending.mergedValues);
		setPending(undefined);
	};

	const handleCancel = () => {
		setPending(undefined);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		transform((data) => ({
			ico: data.ico,
			physical_person: {
				...data.physical_person,
				id: editingPerson?.id,
			},
		}));
		post("/clients/create_individual_entrepreneur");
	};

	return (
		<>
			<Head title="Nový klient - Fyzická osoba" />
			<Dashboard user={user}>
				<form
					onSubmit={handleSubmit}
					className="flex flex-1 flex-col overflow-hidden"
				>
					<div className="overflow-y-auto p-4 lg:p-6">
						<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
							<div>
								<h2 className="font-semibold text-2xl">
									Nový klient – Fyzická osoba (OSVČ)
								</h2>
								<p className="text-muted-foreground text-sm">
									Vyplňte údaje o novém klientovi. Povinná pole jsou označena{" "}
									<span className="text-destructive">*</span>.
								</p>
							</div>

							<Card>
								<CardHeader>
									<CardTitle>
										IČO <span className="text-destructive">*</span>
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="flex gap-2">
										<Field className="flex-1" data-invalid={!!errors.ico}>
											<Input
												id="ico"
												value={data.ico || ""}
												onChange={(e) => {
													clearErrors("ico");
													setData("ico", e.target.value || null);
												}}
												placeholder="12345678"
												aria-invalid={!!errors.ico}
											/>
											<FieldError>{errors.ico}</FieldError>
										</Field>
										<Button
											type="button"
											variant="outline"
											onClick={() => data.ico && aresMutation.mutate(data.ico)}
											disabled={!data.ico || aresMutation.isPending}
										>
											<HugeiconsIcon
												icon={Download01Icon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											{aresMutation.isPending ? "Načítám..." : "Načíst z ARES"}
										</Button>
									</div>
								</CardContent>
							</Card>

							<div>
								<h3 className="font-semibold text-lg">Údaje o osobě</h3>
								<p className="text-muted-foreground text-sm">
									Osobní údaje fyzické osoby podnikající jako OSVČ.
								</p>
							</div>

							{editingPerson && (
								<EditingPersonItem
									person={editingPerson}
									onCancel={() => setEditingPerson(undefined)}
								/>
							)}

							<PersonFormFields
								value={data.physical_person}
								onChange={(person) => {
									setData("physical_person", person);
									clearErrors();
								}}
								errors={errors}
								countries={countries}
								onPersonSelected={(person) => {
									setEditingPerson(person);
									applyOrShowConflicts(
										person.formData,
										`Vybrali jste existující osobu ${person.formData.first_name} ${person.formData.last_name}.`,
									);
								}}
							/>

							{pending && (
								<ConflictDialog
									conflicts={pending.conflicts}
									description={pending.description}
									onApply={handleApply}
									onCancel={handleCancel}
								/>
							)}
						</div>
					</div>

					<div className="border-t bg-background px-4 py-4 lg:px-6">
						<div className="mx-auto flex w-full max-w-2xl">
							<div className="ml-auto flex gap-4">
								<Button
									type="button"
									variant="outline"
									size="lg"
									render={<Link href="/clients" />}
								>
									Zrušit
								</Button>
								{editingPerson ? (
									<Button type="submit" size="lg" disabled={processing}>
										Uložit a přidat
									</Button>
								) : (
									<Button type="submit" size="lg" disabled={processing}>
										Vytvořit klienta
									</Button>
								)}
							</div>
						</div>
					</div>
				</form>
			</Dashboard>
		</>
	);
}
